// ../../../../2026-08-16/are-2/work/PulseSensorPlayground/docs/pulse-transit-time/pulse-transit-time-protocol.mjs?v=20260819-ptt-r1
function integer(value, name, minimum, maximum) {
  if (!Number.isInteger(value) || value < minimum || value > maximum) {
    throw new Error(`invalid ${name}`);
  }
  return value;
}
function unsignedDecimal(field, name, maximum) {
  if (!/^(?:0|[1-9]\d*)$/.test(field)) throw new Error(`invalid ${name}`);
  return integer(Number(field), name, 0, maximum);
}
function parsePulseTransitTimeLine(line) {
  const fields = line.trim().split(",");
  if (fields[0] !== "PTT1") return null;
  if (fields.length !== 4) throw new Error("invalid PTT1 field count");
  const timestampUs = unsignedDecimal(fields[1], "timestamp", 4294967295);
  return {
    source: "pulse-transit-time",
    format: "Pulse Transit Time PTT1",
    mode: "LIVE",
    timestampUs,
    timestampMs: timestampUs / 1e3,
    proximalSignal: unsignedDecimal(fields[2], "proximal signal", 65535),
    distalSignal: unsignedDecimal(fields[3], "distal signal", 65535)
  };
}

// ../../../../2026-08-16/are-2/work/PulseSensorPlayground/docs/signal-coach/signal-coach-core.mjs?v=20260819-ptt-r1
var DEFAULTS = Object.freeze({
  pulseThreshold: 550,
  thresholdMode: "adaptive",
  refractoryMs: 250,
  noBeatTimeoutMs: 3e3,
  staleMs: 1500,
  minBpm: 40,
  maxBpm: 180,
  minIbi: 333,
  maxIbi: 1500,
  minAmplitude: 20,
  qualitySteps: 12,
  qualityLock: 10,
  qualityUp: 3,
  qualityDown: 1,
  qualityFastUp: 6,
  bpmAverageCount: 10,
  rangeSnap: 80,
  rearmRange: 120,
  rearmNoBeatMs: 2200,
  rearmCooldownMs: 3500,
  flatRange: 90,
  flatAmplitude: 12,
  resyncLabelMs: 900,
  resyncFastMs: 6e3,
  beatFlashMs: 200
});
var COACH_COPY = Object.freeze({
  READY: "Connect a pulse-wave stream to begin.",
  NO_SIGNAL: "Check power and the signal wire. Then place the sensor on skin.",
  HOLD_STEADY: "Use light, steady pressure. Do not squeeze the sensor.",
  SEARCHING: "Waveform found. Keep still while Chrome finds the beat pattern.",
  GOOD_WAVE: "Good waveform. Hold this position for a few more beats.",
  LOCKING: "Almost there. Keep your hand relaxed and still.",
  SIGNAL_LOST: "The signal moved. Reposition gently and hold still.",
  QUALIFIED: "Signal looks consistent. Browser BPM is ready.",
  RESYNC: "Chrome retuned to the waveform on screen. Keep still."
});
function clamp(value, minimum, maximum) {
  return Math.max(minimum, Math.min(maximum, value));
}
function nowMs() {
  return globalThis.performance?.now?.() ?? Date.now();
}
var BrowserSignalCoach = class {
  constructor(options = {}) {
    this.options = { ...DEFAULTS, ...options };
    if (!["adaptive", "fixed"].includes(this.options.thresholdMode)) {
      throw new Error("threshold mode must be adaptive or fixed");
    }
    this.reset();
  }
  setThresholdMode(mode, threshold = this.options.pulseThreshold) {
    if (!["adaptive", "fixed"].includes(mode)) throw new Error("threshold mode must be adaptive or fixed");
    if (!Number.isFinite(threshold) || threshold < 0 || threshold > 1023) throw new Error("threshold must be 0..1023");
    this.options.thresholdMode = mode;
    this.options.pulseThreshold = Math.round(threshold);
    this.threshold = this.options.pulseThreshold;
    this.peak = this.threshold;
    this.trough = this.threshold;
    this.pulsing = false;
    return this.lastResult;
  }
  reset(timestamp = null) {
    const start = timestamp ?? 0;
    const initial = this.options.pulseThreshold;
    this.inputDivisor = null;
    this.signal = initial;
    this.minimum = initial - this.options.rangeSnap / 2;
    this.maximum = initial + this.options.rangeSnap / 2;
    this.threshold = initial;
    this.peak = initial;
    this.trough = initial;
    this.pulsing = false;
    this.ibi = 600;
    this.amplitude = 0;
    this.quality = 0;
    this.bpm = 0;
    this.rates = [];
    this.locked = false;
    this.firstBeat = true;
    this.secondBeat = false;
    this.lastBeat = start;
    this.lastQualifiedBeat = null;
    this.lastRearm = start;
    this.rearms = 0;
    this.resyncLabelUntil = -Infinity;
    this.resyncFastUntil = -Infinity;
    this.beatFlashUntil = -Infinity;
    this.sampleCount = 0;
    this.state = "READY";
    this.lastResult = this.snapshot(start, false, false);
    return this.lastResult;
  }
  normalizeSignal(rawSignal) {
    if (!Number.isFinite(rawSignal)) throw new Error("pulse sample must be numeric");
    if (rawSignal < 0 || rawSignal > 65535) throw new Error("pulse sample is outside the supported range");
    if (this.inputDivisor === null) this.inputDivisor = rawSignal > 1300 ? 4 : 1;
    if (rawSignal > 1300) this.inputDivisor = 4;
    return clamp(Math.round(rawSignal / this.inputDivisor), 0, 1023);
  }
  trackRange(signal) {
    this.minimum = Math.min(this.minimum + 1, signal);
    this.maximum = Math.max(this.maximum - 1, signal);
    if (this.maximum - this.minimum < this.options.rangeSnap) {
      this.minimum = signal - this.options.rangeSnap / 2;
      this.maximum = signal + this.options.rangeSnap / 2;
    }
  }
  qualifies(interval) {
    const rate = interval ? Math.floor(6e4 / interval) : 0;
    return rate >= this.options.minBpm && rate <= this.options.maxBpm && interval >= this.options.minIbi && interval <= this.options.maxIbi && this.amplitude >= this.options.minAmplitude;
  }
  smoothedBpm(interval) {
    this.rates.push(interval);
    if (this.rates.length > this.options.bpmAverageCount) this.rates.shift();
    const total = this.rates.reduce((sum, value) => sum + value, 0);
    return Math.floor(6e4 * this.rates.length / total);
  }
  rearm(timestamp) {
    const midpoint = this.options.thresholdMode === "fixed" ? this.options.pulseThreshold : this.maximum > this.minimum ? Math.floor((this.minimum + this.maximum) / 2) : this.options.pulseThreshold;
    this.threshold = midpoint;
    this.peak = midpoint;
    this.trough = midpoint;
    this.pulsing = false;
    this.quality = 0;
    this.bpm = 0;
    this.rates = [];
    this.locked = false;
    this.rearms += 1;
    this.lastRearm = timestamp;
    this.lastBeat = timestamp;
  }
  resync(timestamp = nowMs()) {
    const midpoint = this.options.thresholdMode === "fixed" ? this.options.pulseThreshold : this.maximum > this.minimum ? Math.floor((this.minimum + this.maximum) / 2) : this.options.pulseThreshold;
    this.threshold = midpoint;
    this.peak = midpoint;
    this.trough = midpoint;
    this.pulsing = false;
    this.ibi = 600;
    this.amplitude = Math.max(this.amplitude, this.maximum - this.minimum);
    this.firstBeat = true;
    this.secondBeat = false;
    this.quality = 0;
    this.bpm = 0;
    this.rates = [];
    this.locked = false;
    this.rearms += 1;
    this.lastBeat = timestamp;
    this.lastRearm = timestamp;
    this.lastQualifiedBeat = timestamp;
    this.resyncLabelUntil = timestamp + this.options.resyncLabelMs;
    this.resyncFastUntil = timestamp + this.options.resyncFastMs;
    this.state = "RESYNC";
    this.lastResult = this.snapshot(timestamp, false, false);
    return this.lastResult;
  }
  detect(timestamp) {
    const elapsed = timestamp - this.lastBeat;
    const gate = Math.floor(this.ibi * 3 / 5);
    let candidateBeat = false;
    let qualifiedBeat = false;
    if (this.signal < this.threshold && elapsed > gate && this.signal < this.trough) {
      this.trough = this.signal;
    }
    if (this.signal > this.threshold && this.signal > this.peak) this.peak = this.signal;
    if (elapsed > this.options.refractoryMs && elapsed > gate && this.signal > this.threshold && !this.pulsing) {
      this.pulsing = true;
      candidateBeat = true;
      const interval = elapsed;
      this.lastBeat = timestamp;
      if (this.secondBeat) {
        this.secondBeat = false;
        this.ibi = interval;
      } else if (this.firstBeat) {
        this.firstBeat = false;
        this.secondBeat = true;
      } else {
        this.ibi = interval;
        qualifiedBeat = this.qualifies(interval);
        if (qualifiedBeat) {
          this.bpm = this.smoothedBpm(interval);
          this.lastQualifiedBeat = timestamp;
          const step = timestamp < this.resyncFastUntil ? this.options.qualityFastUp : this.options.qualityUp;
          this.quality = Math.min(this.options.qualitySteps, this.quality + step);
        } else {
          this.quality = Math.max(0, this.quality - this.options.qualityDown);
        }
        this.locked = this.quality >= this.options.qualityLock;
        if (this.locked && qualifiedBeat) this.beatFlashUntil = timestamp + this.options.beatFlashMs;
      }
    }
    if (this.signal < this.threshold && this.pulsing) {
      this.pulsing = false;
      this.amplitude = this.peak - this.trough;
      this.threshold = this.options.thresholdMode === "adaptive" ? this.trough + Math.floor(this.amplitude / 2) : this.options.pulseThreshold;
      this.peak = this.threshold;
      this.trough = this.threshold;
    }
    if (this.lastQualifiedBeat === null || timestamp - this.lastQualifiedBeat > this.options.noBeatTimeoutMs) {
      this.locked = false;
      this.quality = 0;
      this.bpm = 0;
    }
    if (!this.locked && this.maximum - this.minimum >= this.options.rearmRange && timestamp - this.lastBeat >= this.options.rearmNoBeatMs && timestamp - this.lastRearm >= this.options.rearmCooldownMs) {
      this.rearm(timestamp);
    }
    return { candidateBeat, qualifiedBeat };
  }
  coachState(timestamp) {
    const range = this.maximum - this.minimum;
    if (timestamp < this.resyncLabelUntil) return "RESYNC";
    const fresh = this.lastQualifiedBeat !== null && timestamp - this.lastQualifiedBeat <= this.options.staleMs;
    if (fresh && this.locked && this.quality >= this.options.qualitySteps) return "QUALIFIED";
    if (fresh && (this.locked || this.quality > 0)) return "LOCKING";
    if (this.locked) return "SIGNAL_LOST";
    if (range < this.options.flatRange || this.amplitude < this.options.flatAmplitude) return "NO_SIGNAL";
    if (this.amplitude < this.options.minAmplitude) return "HOLD_STEADY";
    if (range >= this.options.rearmRange) return "GOOD_WAVE";
    return "SEARCHING";
  }
  snapshot(timestamp, candidateBeat, qualifiedBeat) {
    return {
      state: this.state,
      quality: Math.round(this.quality),
      locked: this.state === "QUALIFIED",
      bpm: this.state === "QUALIFIED" ? this.bpm : 0,
      ibi: this.state === "QUALIFIED" ? this.ibi : 0,
      beat: this.state === "QUALIFIED" && qualifiedBeat,
      candidateBeat,
      qualifiedBeat,
      amplitude: Math.round(this.amplitude),
      threshold: Math.round(this.threshold),
      thresholdMode: this.options.thresholdMode,
      range: Math.round(this.maximum - this.minimum),
      minimum: Math.round(this.minimum),
      maximum: Math.round(this.maximum),
      signal10: this.signal,
      rearms: this.rearms,
      sampleCount: this.sampleCount,
      timestamp
    };
  }
  update(frameOrSignal, timestamp = nowMs()) {
    const rawSignal = typeof frameOrSignal === "number" ? frameOrSignal : frameOrSignal.signal;
    this.signal = this.normalizeSignal(rawSignal);
    this.sampleCount += 1;
    this.trackRange(this.signal);
    const { candidateBeat, qualifiedBeat } = this.detect(timestamp);
    this.state = this.coachState(timestamp);
    this.lastResult = this.snapshot(timestamp, candidateBeat, qualifiedBeat);
    return this.lastResult;
  }
};

// ../../../../2026-08-16/are-2/work/PulseSensorPlayground/docs/pulse-transit-time/pulse-transit-time-core.mjs?v=20260819-ptt-r1
function median(values) {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
}
var SignalQualityWindow = class {
  constructor({ windowSize = 500, warmupSize = 100, minRange = 25, clipLow = 5, clipHigh = 1018 } = {}) {
    this.options = { windowSize, warmupSize, minRange, clipLow, clipHigh };
    this.samples = [];
  }
  update(signal) {
    this.samples.push(signal);
    if (this.samples.length > this.options.windowSize) this.samples.shift();
    const minimum = Math.min(...this.samples);
    const maximum = Math.max(...this.samples);
    const clipped = this.samples.filter((value) => value <= this.options.clipLow || value >= this.options.clipHigh).length;
    let state = "GOOD";
    if (this.samples.length < this.options.warmupSize) state = "WARMING";
    else if (clipped / this.samples.length >= 0.01) state = "CLIPPED";
    else if (maximum - minimum < this.options.minRange) state = "WEAK";
    return { state, minimum, maximum, range: maximum - minimum, clipped };
  }
};
var PttPairer = class {
  constructor({ minimumMs = 5, maximumMs = 300, historyLength = 20 } = {}) {
    this.minimumMs = minimumMs;
    this.maximumMs = maximumMs;
    this.historyLength = historyLength;
    this.reset();
  }
  reset() {
    this.proximal = null;
    this.values = [];
    this.accepted = 0;
    this.rejected = 0;
    this.unmatched = 0;
  }
  setWindow(minimumMs, maximumMs) {
    if (!Number.isFinite(minimumMs) || !Number.isFinite(maximumMs) || minimumMs < 0 || maximumMs <= minimumMs) {
      throw new Error("PTT window must have a non-negative minimum below the maximum");
    }
    this.minimumMs = minimumMs;
    this.maximumMs = maximumMs;
  }
  pushProximal(timestamp) {
    if (this.proximal !== null) this.unmatched += 1;
    this.proximal = timestamp;
  }
  pushDistal(timestamp, qualityOkay = true) {
    if (this.proximal === null) {
      this.unmatched += 1;
      return null;
    }
    const delay = timestamp - this.proximal;
    if (delay < this.minimumMs) return null;
    this.proximal = null;
    if (delay > this.maximumMs || !qualityOkay) {
      this.rejected += 1;
      return null;
    }
    this.accepted += 1;
    this.values.push(delay);
    if (this.values.length > this.historyLength) this.values.shift();
    return delay;
  }
  expire(timestamp) {
    if (this.proximal !== null && timestamp - this.proximal > this.maximumMs) {
      this.proximal = null;
      this.unmatched += 1;
    }
  }
  snapshot(latest = null) {
    return {
      latest,
      median: median(this.values),
      accepted: this.accepted,
      rejected: this.rejected,
      unmatched: this.unmatched,
      pending: this.proximal !== null
    };
  }
};
var PulseTransitTimeCoach = class {
  constructor(options = {}) {
    const channelDefaults = { pulseThreshold: 550, thresholdMode: "fixed" };
    this.proximal = new BrowserSignalCoach({ ...channelDefaults, ...options.proximal });
    this.distal = new BrowserSignalCoach({ ...channelDefaults, ...options.distal });
    this.proximalQuality = new SignalQualityWindow(options.quality);
    this.distalQuality = new SignalQualityWindow(options.quality);
    this.pairer = new PttPairer(options.pairing);
  }
  configureChannel(channel, mode, threshold) {
    const detector = channel === "proximal" ? this.proximal : this.distal;
    detector.setThresholdMode(mode, threshold);
  }
  setPairingWindow(minimumMs, maximumMs) {
    this.pairer.setWindow(minimumMs, maximumMs);
  }
  update(proximalSignal, distalSignal, timestamp) {
    const proximal = this.proximal.update(proximalSignal, timestamp);
    const distal = this.distal.update(distalSignal, timestamp);
    const proximalQuality2 = this.proximalQuality.update(proximal.signal10);
    const distalQuality2 = this.distalQuality.update(distal.signal10);
    this.pairer.expire(timestamp);
    if (proximal.qualifiedBeat) this.pairer.pushProximal(timestamp);
    let latest = null;
    if (distal.qualifiedBeat) {
      latest = this.pairer.pushDistal(timestamp, proximalQuality2.state === "GOOD" && distalQuality2.state === "GOOD");
    }
    return {
      proximal,
      distal,
      proximalQuality: proximalQuality2,
      distalQuality: distalQuality2,
      ptt: this.pairer.snapshot(latest)
    };
  }
};

// ../../../../2026-08-16/are-2/work/PulseSensorPlayground/docs/pulse-transit-time/pulse-transit-time.mjs
var pageOptions = new URLSearchParams(location.search);
if (window.self !== window.top || pageOptions.get("embedded") === "1") {
  document.documentElement.classList.add("is-embedded");
}
var HISTORY_LENGTH = 600;
var connectBtn = document.querySelector("#connectBtn");
var statusDot = document.querySelector("#statusDot");
var statusText = document.querySelector("#statusText");
var sourceMeta = document.querySelector("#sourceMeta");
var errorMessage = document.querySelector("#errorMessage");
var simulationWarning = document.querySelector("#simulationWarning");
var proximalCanvas = document.querySelector("#proximalCanvas");
var distalCanvas = document.querySelector("#distalCanvas");
var proximalThresholdMode = document.querySelector("#proximalThresholdMode");
var distalThresholdMode = document.querySelector("#distalThresholdMode");
var proximalThreshold = document.querySelector("#proximalThreshold");
var distalThreshold = document.querySelector("#distalThreshold");
var minimumPtt = document.querySelector("#minimumPtt");
var maximumPtt = document.querySelector("#maximumPtt");
var proximalQuality = document.querySelector("#proximalQuality");
var distalQuality = document.querySelector("#distalQuality");
var proximalMeta = document.querySelector("#proximalMeta");
var distalMeta = document.querySelector("#distalMeta");
var latestPtt = document.querySelector("#latestPtt");
var medianPtt = document.querySelector("#medianPtt");
var acceptedPairs = document.querySelector("#acceptedPairs");
var rejectedPairs = document.querySelector("#rejectedPairs");
var pttGuidance = document.querySelector("#pttGuidance");
var modeChip = document.querySelector("#modeChip");
var frameMeta = document.querySelector("#frameMeta");
var signalMeta = document.querySelector("#signalMeta");
var rawSerial = document.querySelector("#rawSerial");
var copySketchBtn = document.querySelector("#copySketchBtn");
var copySketchStatus = document.querySelector("#copySketchStatus");
var unoR4Sketch = document.querySelector("#unoR4Sketch");
var settingsControls = [proximalThresholdMode, distalThresholdMode, proximalThreshold, distalThreshold, minimumPtt, maximumPtt];
var port = null;
var reader = null;
var readTask = null;
var reading = false;
var coach = new PulseTransitTimeCoach();
var proximalHistory = new Array(HISTORY_LENGTH).fill(null);
var distalHistory = new Array(HISTORY_LENGTH).fill(null);
var lastResult = null;
var lastFrame = null;
var frameCount = 0;
var streamTimestamp = null;
var lastDeviceTimestamp = null;
var lastRenderAt = -Infinity;
function qualityLabel(target, quality) {
  target.textContent = quality.state;
  target.className = quality.state === "GOOD" ? "good" : quality.state === "CLIPPED" ? "clipped" : "";
}
function drawWaveform(target, values, threshold, color) {
  const rectangle = target.getBoundingClientRect();
  if (!rectangle.width || !rectangle.height) return;
  const dpr = window.devicePixelRatio || 1;
  const width = Math.max(1, Math.round(rectangle.width * dpr));
  const height = Math.max(1, Math.round(rectangle.height * dpr));
  const drawing = target.getContext("2d");
  if (target.width !== width || target.height !== height) {
    target.width = width;
    target.height = height;
    drawing.setTransform(dpr, 0, 0, dpr, 0, 0);
  }
  const cssWidth = rectangle.width;
  const cssHeight = rectangle.height;
  drawing.fillStyle = "#07110f";
  drawing.fillRect(0, 0, cssWidth, cssHeight);
  drawing.strokeStyle = "#25352e";
  drawing.lineWidth = 1;
  for (let index = 1; index < 4; index += 1) {
    drawing.beginPath();
    drawing.moveTo(0, cssHeight * index / 4);
    drawing.lineTo(cssWidth, cssHeight * index / 4);
    drawing.stroke();
  }
  const present = values.filter((value) => value !== null);
  if (present.length < 2) return;
  const low = Math.max(0, Math.min(...present, threshold) - 35);
  const high = Math.min(1023, Math.max(...present, threshold) + 35);
  const range = Math.max(1, high - low);
  const thresholdY = cssHeight - (threshold - low) / range * cssHeight;
  drawing.save();
  drawing.setLineDash([6, 5]);
  drawing.strokeStyle = "#ff816d";
  drawing.beginPath();
  drawing.moveTo(0, thresholdY);
  drawing.lineTo(cssWidth, thresholdY);
  drawing.stroke();
  drawing.restore();
  drawing.strokeStyle = color;
  drawing.lineWidth = 2.5;
  drawing.lineJoin = "round";
  drawing.beginPath();
  let started = false;
  values.forEach((value, index) => {
    if (value === null) return;
    const x = index / (HISTORY_LENGTH - 1) * cssWidth;
    const y = cssHeight - (value - low) / range * cssHeight;
    if (!started) {
      drawing.moveTo(x, y);
      started = true;
    } else {
      drawing.lineTo(x, y);
    }
  });
  drawing.stroke();
}
function resizeCanvases() {
  drawWaveform(proximalCanvas, proximalHistory, lastResult?.proximal?.threshold ?? Number(proximalThreshold.value), "#55d6e3");
  drawWaveform(distalCanvas, distalHistory, lastResult?.distal?.threshold ?? Number(distalThreshold.value), "#76f7c2");
}
function applySettings() {
  coach.configureChannel("proximal", proximalThresholdMode.value, Number(proximalThreshold.value));
  coach.configureChannel("distal", distalThresholdMode.value, Number(distalThreshold.value));
  coach.setPairingWindow(Number(minimumPtt.value), Number(maximumPtt.value));
}
function clearSession() {
  coach = new PulseTransitTimeCoach();
  proximalHistory = new Array(HISTORY_LENGTH).fill(null);
  distalHistory = new Array(HISTORY_LENGTH).fill(null);
  lastResult = null;
  lastFrame = null;
  frameCount = 0;
  streamTimestamp = null;
  lastDeviceTimestamp = null;
  lastRenderAt = -Infinity;
  qualityLabel(proximalQuality, { state: "WARMING" });
  qualityLabel(distalQuality, { state: "WARMING" });
  proximalMeta.textContent = `Signal -- \xB7 threshold ${proximalThreshold.value} ${proximalThresholdMode.value}`;
  distalMeta.textContent = `Signal -- \xB7 threshold ${distalThreshold.value} ${distalThresholdMode.value}`;
  latestPtt.textContent = "--";
  medianPtt.textContent = "--";
  acceptedPairs.textContent = "0";
  rejectedPairs.textContent = "0 / 0";
  pttGuidance.textContent = "Upload the Pulse Transit Time sender, then connect both sensors.";
  modeChip.textContent = "WAITING";
  modeChip.className = "";
  frameMeta.textContent = "No synchronized samples received";
  signalMeta.textContent = "A0 -- \xB7 A1 --";
  rawSerial.textContent = "waiting for PTT1 data...";
  resizeCanvases();
}
function resetSession() {
  clearSession();
  applySettings();
}
function nextTimestamp(frame, explicitTimestamp = null) {
  if (explicitTimestamp !== null) {
    streamTimestamp = explicitTimestamp;
    return streamTimestamp;
  }
  const deviceTimestamp = frame.timestampMs;
  if (lastDeviceTimestamp === null) streamTimestamp = deviceTimestamp;
  else if (deviceTimestamp > lastDeviceTimestamp) streamTimestamp += deviceTimestamp - lastDeviceTimestamp;
  else streamTimestamp += 2;
  lastDeviceTimestamp = deviceTimestamp;
  return streamTimestamp;
}
function updateFrame(frame, line, explicitTimestamp = null) {
  const timestamp = nextTimestamp(frame, explicitTimestamp);
  frameCount += 1;
  lastFrame = frame;
  lastResult = coach.update(frame.proximalSignal, frame.distalSignal, timestamp);
  proximalHistory.shift();
  proximalHistory.push(lastResult.proximal.signal10);
  distalHistory.shift();
  distalHistory.push(lastResult.distal.signal10);
  if (timestamp - lastRenderAt < 33 && lastResult.ptt.latest === null) return;
  lastRenderAt = timestamp;
  resizeCanvases();
  qualityLabel(proximalQuality, lastResult.proximalQuality);
  qualityLabel(distalQuality, lastResult.distalQuality);
  proximalMeta.textContent = `Signal ${lastResult.proximal.signal10} \xB7 threshold ${lastResult.proximal.threshold} ${lastResult.proximal.thresholdMode}`;
  distalMeta.textContent = `Signal ${lastResult.distal.signal10} \xB7 threshold ${lastResult.distal.threshold} ${lastResult.distal.thresholdMode}`;
  if (lastResult.ptt.latest !== null) latestPtt.textContent = lastResult.ptt.latest.toFixed(1);
  medianPtt.textContent = lastResult.ptt.median === null ? "--" : lastResult.ptt.median.toFixed(1);
  acceptedPairs.textContent = String(lastResult.ptt.accepted);
  rejectedPairs.textContent = `${lastResult.ptt.rejected} / ${lastResult.ptt.unmatched}`;
  if (lastResult.proximalQuality.state === "CLIPPED" || lastResult.distalQuality.state === "CLIPPED") {
    pttGuidance.textContent = "A channel is clipping at the ADC rail. Reduce pressure, check power, and reposition before trusting PTT.";
  } else if (lastResult.proximalQuality.state !== "GOOD" || lastResult.distalQuality.state !== "GOOD") {
    pttGuidance.textContent = "Hold both sensors lightly and still until both channels report GOOD.";
  } else if (lastResult.ptt.accepted === 0) {
    pttGuidance.textContent = "Both waves look usable. Waiting for repeatable same-cycle beat pairs.";
  } else {
    pttGuidance.textContent = "Both channels pass signal quality. PTT is an educational timing measurement, not blood pressure.";
  }
  const simulated = frame.mode === "SIM";
  statusDot.className = `status-dot ${simulated ? "simulation" : "connected"}`;
  statusText.textContent = simulated ? "Simulated two-sensor replay" : "Two PulseSensors connected";
  sourceMeta.textContent = `${frame.format} \xB7 250000 baud`;
  simulationWarning.hidden = !simulated;
  modeChip.textContent = simulated ? "SIM PTT" : "LIVE PTT";
  modeChip.className = simulated ? "sim" : "live";
  frameMeta.textContent = `${frameCount.toLocaleString()} synchronized dual samples`;
  signalMeta.textContent = `A0 ${lastResult.proximal.signal10} \xB7 A1 ${lastResult.distal.signal10}`;
  rawSerial.textContent = line;
}
function showError(message) {
  errorMessage.textContent = message;
  errorMessage.hidden = false;
}
async function readLoop() {
  const decoder = new TextDecoder();
  let buffer = "";
  while (reading && port?.readable) {
    const activeReader = port.readable.getReader();
    reader = activeReader;
    try {
      while (reading) {
        const { value, done } = await activeReader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split(/\r?\n/);
        buffer = lines.pop() ?? "";
        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed) continue;
          rawSerial.textContent = trimmed;
          try {
            const frame = parsePulseTransitTimeLine(trimmed);
            if (frame) updateFrame(frame, trimmed);
          } catch (error) {
            console.warn("Ignored invalid Pulse Transit Time frame:", error.message);
          }
        }
      }
    } finally {
      activeReader.releaseLock();
      if (reader === activeReader) reader = null;
    }
  }
}
async function finalizeConnection(targetPort) {
  if (!targetPort || targetPort !== port) return false;
  reading = false;
  port = null;
  const activeReader = reader;
  const activeReadTask = readTask;
  if (activeReader) {
    try {
      await activeReader.cancel();
    } catch {
    }
  }
  if (activeReadTask) {
    try {
      await activeReadTask;
    } catch {
    }
  }
  if (readTask === activeReadTask) readTask = null;
  try {
    await targetPort.close();
  } catch {
  }
  connectBtn.textContent = "Connect two-sensor stream";
  settingsControls.forEach((control) => {
    control.disabled = false;
  });
  statusDot.className = "status-dot";
  statusText.textContent = "Disconnected";
  sourceMeta.textContent = "PTT1 dual stream \xB7 250000 baud";
  simulationWarning.hidden = true;
  clearSession();
  return true;
}
async function connect() {
  if (!("serial" in navigator)) {
    showError("Web Serial is unavailable. Use desktop Chrome, Edge, or Brave over HTTPS.");
    return;
  }
  let requestedPort = null;
  try {
    requestedPort = await navigator.serial.requestPort();
    port = requestedPort;
    await requestedPort.open({ baudRate: 25e4, bufferSize: 16384 });
    resetSession();
    reading = true;
    errorMessage.hidden = true;
    connectBtn.textContent = "Disconnect";
    statusText.textContent = "Waiting for synchronized samples";
    sourceMeta.textContent = "Connected \xB7 USB serial \xB7 250000 baud";
    readTask = readLoop();
    await readTask;
    if (port === requestedPort) await finalizeConnection(requestedPort);
  } catch (error) {
    if (requestedPort && port === requestedPort) await finalizeConnection(requestedPort);
    if (error.name !== "NotFoundError") showError(`Connection failed: ${error.message}`);
  }
}
connectBtn.addEventListener("click", () => {
  if (port) finalizeConnection(port).catch((error) => showError(error.message));
  else connect().catch((error) => showError(error.message));
});
settingsControls.forEach((control) => {
  control.addEventListener("change", () => {
    try {
      resetSession();
      errorMessage.hidden = true;
    } catch (error) {
      showError(error.message);
    }
  });
});
copySketchBtn.addEventListener("click", async () => {
  const sketch = unoR4Sketch.textContent.trim();
  try {
    await navigator.clipboard.writeText(sketch);
    copySketchBtn.textContent = "Copied";
    copySketchStatus.textContent = "Paste into a new Arduino sketch.";
  } catch {
    const selection = window.getSelection();
    const range = document.createRange();
    range.selectNodeContents(unoR4Sketch);
    selection.removeAllRanges();
    selection.addRange(range);
    copySketchStatus.textContent = "Sketch selected. Press Command-C to copy.";
  }
  setTimeout(() => {
    copySketchBtn.textContent = "Copy sketch";
  }, 1800);
});
navigator.serial?.addEventListener("disconnect", (event) => {
  finalizeConnection(event.target).catch(() => {
  });
});
window.addEventListener("resize", resizeCanvases);
function pulseSample(timestamp, delay = 0) {
  const phase = ((timestamp - delay) % 833 + 833) % 833;
  if (phase < 50) return 500 + Math.round(phase * 3);
  if (phase < 140) return 650 - Math.round((phase - 50) * 1.5);
  return 500;
}
function startReplay() {
  connectBtn.hidden = true;
  settingsControls.forEach((control) => {
    control.disabled = true;
  });
  resetSession();
  let timestamp = 0;
  const timer = setInterval(() => {
    for (let sample = 0; sample < 10; sample += 1) {
      const frame = {
        source: "bench",
        format: "Browser bench PTT1",
        mode: "SIM",
        timestampUs: timestamp * 1e3,
        timestampMs: timestamp,
        proximalSignal: pulseSample(timestamp),
        distalSignal: pulseSample(timestamp, 40)
      };
      updateFrame(frame, `PTT1,${frame.timestampUs},${frame.proximalSignal},${frame.distalSignal}`, timestamp);
      timestamp += 2;
    }
  }, 20);
  return timer;
}
window.__PULSE_TRANSIT_TIME_QA__ = {
  inject: (line, timestamp = null) => {
    const frame = parsePulseTransitTimeLine(line);
    if (frame) updateFrame(frame, line, timestamp);
    return { frame, result: lastResult };
  },
  reset: resetSession,
  getState: () => ({
    frameCount,
    lastFrame,
    result: lastResult,
    canvasWidths: [proximalCanvas.width, distalCanvas.width],
    connected: Boolean(port),
    button: connectBtn.textContent,
    status: statusText.textContent,
    source: sourceMeta.textContent,
    controlsDisabled: settingsControls.some((control) => control.disabled)
  })
};
resetSession();
if (pageOptions.get("bench") === "1") startReplay();
